export interface TokenStore {
  get(): string | undefined;
  set(token: string, expiresAt: string): void;
  clear(): void;
  /** Epoch ms the current token expires at, or `undefined` if there's no
      token or it predates expiry tracking (a bare-string legacy value). */
  getExpiry(): number | undefined;
}

interface StoredSession {
  token: string;
  expiresAt?: string;
}

export function createTokenStore(storageKey = "ferrivia.session"): TokenStore {
  // Bare string was the only shape written before session expiry tracking
  // existed — read it as a token with no known expiry rather than migrating
  // it on read, so e2e's existing bare-string fixture keeps working.
  function read(): StoredSession | undefined {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return undefined;
      if (raw.startsWith("{")) {
        return JSON.parse(raw) as StoredSession;
      }
      return { token: raw };
    } catch {
      return undefined;
    }
  }

  function get(): string | undefined {
    return read()?.token;
  }

  function set(token: string, expiresAt: string): void {
    try {
      localStorage.setItem(storageKey, JSON.stringify({ token, expiresAt } satisfies StoredSession));
    } catch {
      // localStorage unavailable (private browsing, disabled storage) — the
      // session just won't survive a reload.
    }
  }

  function clear(): void {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // see set
    }
  }

  function getExpiry(): number | undefined {
    const expiresAt = read()?.expiresAt;
    if (!expiresAt) return undefined;
    const ms = Date.parse(expiresAt);
    return Number.isNaN(ms) ? undefined : ms;
  }

  return { get, set, clear, getExpiry };
}
